import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { getEdicao, getEdicaoEfetividade } from '@/lib/api';
import { useDateRange, useDateRangeKey } from '@/lib/dateRange';

interface CasoEditado {
  processo_id: number;
  org_id: number | null;
  n_editadas: number;
  reprocessos: number;
  tem_pdf: boolean;
  status: string | null;
  filename: string | null;
  created_at: string | null;
  n_ficha?: number;
  n_revisao?: number;
  via?: 'revisao' | 'ficha' | 'ambos';
}

/** Bloco da Ficha de liquidação (admin-api/routers/edicao_ficha.py). */
interface FichaBloco {
  disponivel: boolean;
  motivo?: string;
  edicoes: number;
  casos: number;
  orgs: number;
  autores: number;
  marcacoes_conferencia: number;
  por_alvo: Array<{ chave: string; rotulo: string; n: number }>;
  por_familia: Array<{ chave: string; rotulo: string; n: number }>;
  por_secao: Array<{ chave: string; rotulo: string; n: number }>;
  por_autor: Array<{ chave: string; rotulo: string; n: number }>;
  conceitos: Array<{
    alvo: string;
    rotulo_alvo: string;
    rotulo: string;
    campo_id_exemplo: string;
    n: number;
    n_casos: number;
    n_orgs: number;
  }>;
  casos_lista: Array<{ processo_id: number; org_id: number | null; n_edicoes: number; alvos: string[] }>;
  edicoes_invisiveis_antes: number;
  pct_invisiveis_antes: number | null;
  alvos_distintos: string[];
  uniao_casos?: {
    editados: number;
    so_revisao: number;
    so_ficha: number;
    ambos: number;
    orgs_editaram: number;
    orgs_so_revisao: number;
    orgs_so_ficha: number;
    orgs_ambos: number;
  };
}

interface Agg {
  uso: {
    casos_editados: number;
    orgs_editaram: number;
    casos_editados_revisao?: number;
    orgs_editaram_revisao?: number;
    total_casos: number;
    total_orgs: number;
    pct_casos_editados: number | null;
    pct_orgs_editam: number | null;
  };
  reprocesso: {
    casos_reprocessados: number;
    total_reprocessos: number;
    distribuicao: Record<string, number>;
  };
  funciona: {
    sucesso: number;
    reemitiu: number;
    reemitiu_novo: number;
    abandono: number;
    taxa_sucesso: number | null;
    taxa_abandono: number | null;
  };
  monetizacao: {
    reprocessos_cobrados: number;
    creditos_reprocesso: number;
  };
  casos: CasoEditado[];
  conceitos?: Array<{ concept: string; n_casos: number; n_orgs: number; n_vazios: number }>;
  ficha?: FichaBloco;
}

const CONCEITO_LABEL: Record<string, string> = {
  salario_base_act: 'Base salarial (ACT)',
  piso_salarial_cct: 'Piso salarial (CCT)',
  'Maior Remuneração': 'Maior Remuneração',
  'Última Remuneração': 'Última Remuneração',
  data_admissao: 'Data de admissão',
  data_demissao: 'Data de demissão',
  'Data de Admissão': 'Data de admissão',
  'Data de Demissão': 'Data de demissão',
  motivo_desligamento: 'Motivo do desligamento',
  'Percentual Honorários Advocatícios': 'Honorários (%)',
};

/** Ficha de liquidação — a fonte de edição que a aba não enxergava.
 *  A ficha grava onde o executor lê: Variavel, parâmetro/reflexo/inclusão/desfecho de verba,
 *  histórico salarial e modo do cálculo. Só `variavel` já era contado acima; o resto é ganho
 *  real de cobertura (`edicoes_invisiveis_antes`). */
function FichaBlocoView({ f, totalCasos, totalOrgs }: {
  f: FichaBloco;
  totalCasos: number;
  totalOrgs: number;
}) {
  if (!f.disponivel) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-sm font-semibold text-slate-700">Ficha de liquidação</div>
        <p className="text-xs text-slate-400 mt-1">
          Sem medição disponível: {f.motivo || 'tabela ficha_edicao ausente neste ambiente'}.
        </p>
      </div>
    );
  }

  const un = f.uniao_casos;
  const pctCasos = un && totalCasos ? Math.round((100 * un.editados) / totalCasos) : null;
  const pctOrgs = un && totalOrgs ? Math.round((100 * un.orgs_editaram) / totalOrgs) : null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100">
        <h2 className="text-sm font-semibold text-slate-700">Ficha de liquidação</h2>
        <p className="text-xs text-slate-500">
          Edições gravadas pela ficha, por onde ela escreve. "Só pela revisão" desconta a variável que a
          própria ficha gravou; um caso editado pelas duas vias conta uma vez. Marcações sem mudança de
          valor e salvamentos com o mesmo valor não contam.
        </p>
      </div>

      {/* cartões: uso total (união) + o quanto a ficha acrescentou */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4">
        <div className="rounded-lg border border-slate-200 p-3">
          <div className="text-xs text-slate-500">Casos com edição na ficha</div>
          <div className="text-2xl font-bold text-slate-800">{f.casos}</div>
          <div className="text-xs text-slate-400 mt-1">
            {pctCasos != null ? `união revisão ∪ ficha: ${un!.editados} (${pctCasos}% de ${totalCasos})` : `na janela`}
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 p-3">
          <div className="text-xs text-slate-500">Orgs com edição na ficha</div>
          <div className="text-2xl font-bold text-slate-800">{f.orgs}</div>
          <div className="text-xs text-slate-400 mt-1">
            {pctOrgs != null ? `união: ${un!.orgs_editaram} (${pctOrgs}% de ${totalOrgs})` : `na janela`}
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 p-3">
          <div className="text-xs text-slate-500">Edições gravadas pela ficha</div>
          <div className="text-2xl font-bold text-slate-800">{f.edicoes}</div>
          <div className="text-xs text-slate-400 mt-1">
            {f.autores} autor(es)
            {f.marcacoes_conferencia > 0 && ` · ${f.marcacoes_conferencia} marcação(ões) sem mudança de valor`}
          </div>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className="text-xs text-amber-700">Invisíveis antes desta fonte</div>
          <div className="text-2xl font-bold text-amber-700">{f.edicoes_invisiveis_antes}</div>
          <div className="text-xs text-amber-700/70 mt-1">
            {f.pct_invisiveis_antes != null ? `${f.pct_invisiveis_antes}% das edições` : '—'} não escrevem em <code>variavel</code>
          </div>
        </div>
      </div>

      {/* de onde veio a edição: só revisão × só ficha × ambos */}
      {un && (
        <div className="px-4 pb-4">
          <div className="text-xs text-slate-500 mb-2">De onde veio a edição</div>
          <div className="flex flex-wrap gap-2">
            {[
              { rotulo: 'Só pela revisão', n: un.so_revisao, orgs: un.orgs_so_revisao },
              { rotulo: 'Só pela ficha', n: un.so_ficha, orgs: un.orgs_so_ficha },
              { rotulo: 'Pelas duas vias', n: un.ambos, orgs: un.orgs_ambos },
            ].map((x) => (
              <div key={x.rotulo} className="rounded-lg border border-slate-200 px-3 py-2">
                <div className="text-xs text-slate-600">{x.rotulo}</div>
                <div className="text-lg font-semibold text-slate-800">
                  {x.n} <span className="text-xs font-normal text-slate-400">casos · {x.orgs} orgs</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* por alvo de escrita — onde a ficha grava de fato */}
      {f.por_alvo.length > 0 && (
        <div className="border-t border-slate-100">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-4 py-2 text-left">Onde a edição é gravada</th>
                <th className="px-3 py-2 text-right">Edições</th>
                <th className="px-3 py-2 text-left">Já era contado</th>
              </tr>
            </thead>
            <tbody>
              {f.por_alvo.map((a) => (
                <tr key={a.chave} className="border-t border-slate-100">
                  <td className="px-4 py-2 text-slate-700">{a.rotulo}</td>
                  <td className="px-3 py-2 text-right font-semibold text-slate-800">{a.n}</td>
                  <td className="px-3 py-2 text-xs">
                    {a.chave === 'variavel'
                      ? <span className="text-slate-400">sim</span>
                      : <span className="text-amber-600">não — ganho de cobertura</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* conceitos (rótulos) mais editados pela ficha */}
      {f.conceitos.length > 0 && (
        <div className="border-t border-slate-100">
          <div className="px-4 py-2.5 text-xs text-slate-500">Campos mais editados pela ficha</div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-4 py-2 text-left">Campo</th>
                <th className="px-3 py-2 text-left">Onde grava</th>
                <th className="px-3 py-2 text-center">Casos</th>
                <th className="px-3 py-2 text-center">Orgs</th>
                <th className="px-3 py-2 text-center">Edições</th>
              </tr>
            </thead>
            <tbody>
              {f.conceitos.slice(0, 30).map((c) => (
                <tr key={`${c.alvo}:${c.rotulo}`} className="border-t border-slate-100">
                  <td className="px-4 py-2 text-slate-700">{c.rotulo}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{c.rotulo_alvo}</td>
                  <td className="px-3 py-2 text-center font-semibold text-slate-800">{c.n_casos}</td>
                  <td className="px-3 py-2 text-center text-slate-600">{c.n_orgs}</td>
                  <td className="px-3 py-2 text-center text-slate-600">{c.n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Bloco EFETIVIDADE (admin-api GET /edicao/efetividade).
 *  Janela pela data da AÇÃO (`dias`), não pela abertura do caso como o resto da aba. */
interface Efetividade {
  disponivel: boolean;
  motivo?: string;
  janela_dias: number;
  tem_sem_efeito?: boolean;
  alteracoes: {
    salvas?: number;
    honradas?: number;
    nao_honradas?: number;
    sem_efeito?: number | null;
    sem_marca?: number;
    marcadas?: number;
    pct_honradas?: number | null;
    pct_nao_honradas?: number | null;
    casos?: number;
    orgs?: number;
  };
  nao_honrados: Array<{
    campo_id: string;
    rotulo: string;
    secao: string | null;
    n: number;
    casos: number;
    orgs: number;
    alvo: string;
    rotulo_alvo: string;
  }>;
  recalculo: { editados?: number; recalcularam?: number; recalc_concluidos?: number; pct_recalcularam?: number | null };
  execucoes: {
    concluidos?: number;
    empates?: number;
    mudou_liquido?: number;
    primeira_medicao?: number;
    cobrados?: number;
    cobrados_com_empate?: number;
    creditos?: number;
    casos?: number;
    comparaveis?: number;
    pct_empate?: number | null;
  };
  ledger: { cobrados?: number; creditos?: number; casos?: number; sem_ajuste_pendente?: number; aproximacao?: string };
  funil: Array<{ chave: string; etapa: string; orgs: number; pct_topo: number | null; pct_anterior: number | null }>;
  eventos: Record<string, { evento: string; n: number; orgs: number; n_nao_honrado: number; primeiro: string | null }>;
  bloqueios: Array<{ code: string; n: number; orgs: number }>;
  cta: Array<{ surface: string; shown: number; clicked: number; orgs: number; pct_clique: number | null }>;
  casos: Array<{
    processo_id: number | null;
    org_id: number | null;
    n_alteracoes: number;
    n_honradas: number;
    n_nao_honradas: number;
    n_sem_marca: number;
    recalculou: boolean;
    n_recalc_concluidos: number;
    liq_ultimo: number | null;
    liq_anterior: number | null;
    delta_liquido: number | null;
    delta_comparavel: boolean;
    empate: boolean | null;
    cobrado: boolean;
    n_cobrancas_ledger: number;
    bloqueio: boolean;
    n_bloqueios: number;
    ultima_alteracao: string | null;
  }>;
  nota?: string;
}

const EVENTO_LABEL: Record<string, string> = {
  ficha_nota_vista: 'Avisos vistos',
  ficha_aponta_para_clicked: 'Cliques em "Editar em"',
  ficha_recalc_bloqueado: 'Recálculos bloqueados',
  ficha_bloqueio_cta: 'CTA do bloqueio',
  ficha_edit_descartada: 'Edições descartadas',
};

const brl = (v: number | null | undefined) =>
  v == null ? '—' : v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function Cartao({ titulo, valor, rodape, tom }: {
  titulo: string;
  valor: ReactNode;
  rodape?: ReactNode;
  tom?: 'neutro' | 'alerta' | 'ok';
}) {
  const cls =
    tom === 'alerta' ? 'border-amber-200 bg-amber-50' : tom === 'ok' ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white';
  const txt = tom === 'alerta' ? 'text-amber-700' : tom === 'ok' ? 'text-emerald-700' : 'text-slate-800';
  return (
    <div className={`rounded-lg border p-3 ${cls}`}>
      <div className={`text-xs ${tom ? txt : 'text-slate-500'}`}>{titulo}</div>
      <div className={`text-2xl font-bold ${txt}`}>{valor}</div>
      {rodape != null && <div className={`text-xs mt-1 ${tom ? `${txt}/70` : 'text-slate-400'}`}>{rodape}</div>}
    </div>
  );
}

/** Efetividade da edição + funil da ficha + últimos casos editados.
 *  Só fatos sobre o NOSSO sistema (honrou/não, mudou/não, cobrou/não) — nenhum juízo sobre
 *  o caso do cliente (regra "ficha sem diagnóstico"). */
function EfetividadeView({ e, dias, onDias }: { e: Efetividade; dias: number; onDias: (d: number) => void }) {
  const seletor = (
    <div className="flex items-center gap-1">
      {[7, 30, 60, 90].map((d) => (
        <button
          key={d}
          onClick={() => onDias(d)}
          className={
            d === dias
              ? 'text-xs px-2 py-1 rounded bg-slate-800 text-white'
              : 'text-xs px-2 py-1 rounded border border-slate-200 text-slate-500 hover:bg-slate-50'
          }
        >
          {d}d
        </button>
      ))}
    </div>
  );

  if (!e.disponivel) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-700">Efetividade da edição</div>
          {seletor}
        </div>
        <p className="text-xs text-slate-400 mt-1">Sem medição disponível: {e.motivo || 'fonte ausente neste ambiente'}.</p>
      </div>
    );
  }

  const a = e.alteracoes || {};
  const r = e.recalculo || {};
  const x = e.execucoes || {};
  const l = e.ledger || {};
  const ev = e.eventos || {};
  const notas = ev.ficha_nota_vista;

  return (
    <div className="space-y-5">
      {/* ---------------- 1. Efetividade ---------------- */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-slate-700">Efetividade da edição</h2>
            <p className="text-xs text-slate-500">
              Salvar não é aplicar. "Honrada" = o motor gravou a alteração; "não honrada" = a alteração foi salva e não
              chegou ao cálculo. Janela pela data da <b>ação</b> ({e.janela_dias} dias), não pela abertura do caso.
            </p>
          </div>
          {seletor}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4">
          <Cartao
            titulo="Alterações salvas"
            valor={a.salvas ?? 0}
            rodape={`${a.casos ?? 0} caso(s) · ${a.orgs ?? 0} org(s)`}
          />
          <Cartao
            titulo="Honradas pelo motor"
            valor={a.honradas ?? 0}
            tom="ok"
            rodape={a.pct_honradas != null ? `${a.pct_honradas}% das ${a.marcadas ?? 0} com marca` : 'sem base com marca'}
          />
          <Cartao
            titulo="Não honradas"
            valor={a.nao_honradas ?? 0}
            tom="alerta"
            rodape={a.pct_nao_honradas != null ? `${a.pct_nao_honradas}% das ${a.marcadas ?? 0} com marca` : 'sem base com marca'}
          />
          <Cartao
            titulo="Sem efeito"
            valor={a.sem_efeito == null ? '—' : a.sem_efeito}
            rodape={
              a.sem_efeito == null
                ? 'coluna ficha_edicao.sem_efeito ainda não existe'
                : 'alteração aplicada que não mudou o resultado'
            }
          />
        </div>

        {(a.sem_marca ?? 0) > 0 && (
          <div className="px-4 pb-3 -mt-1">
            <p className="text-xs text-slate-400">
              {a.sem_marca} alteração(ões) <b>sem marca</b>: gravadas antes da coluna <code>honrado</code> existir
              (09/10). Não contam como honradas nem como não honradas — por isso as taxas usam só as{' '}
              {a.marcadas ?? 0} com marca.
            </p>
          </div>
        )}

        {/* top 10 campos não honrados */}
        <div className="border-t border-slate-100">
          <div className="px-4 py-2.5 text-xs text-slate-500">
            Campos que mais deixam de ser honrados (top 10)
          </div>
          {e.nao_honrados.length === 0 ? (
            <p className="px-4 pb-3 text-xs text-slate-400">
              Nenhuma alteração marcada como não honrada na janela.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-400">
                <tr>
                  <th className="px-4 py-2 text-left">Campo</th>
                  <th className="px-3 py-2 text-left">Onde a ficha grava</th>
                  <th className="px-3 py-2 text-center">Ocorrências</th>
                  <th className="px-3 py-2 text-center">Casos</th>
                  <th className="px-3 py-2 text-center">Orgs</th>
                </tr>
              </thead>
              <tbody>
                {e.nao_honrados.map((c) => (
                  <tr key={c.campo_id} className="border-t border-slate-100">
                    <td className="px-4 py-2 text-slate-700">
                      {c.rotulo}
                      <span className="ml-2 text-xs text-slate-400">{c.campo_id}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{c.rotulo_alvo}</td>
                    <td className="px-3 py-2 text-center font-semibold text-amber-700">{c.n}</td>
                    <td className="px-3 py-2 text-center text-slate-600">{c.casos}</td>
                    <td className="px-3 py-2 text-center text-slate-600">{c.orgs}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* recálculo + empates + cobrança */}
        <div className="border-t border-slate-100 grid grid-cols-1 md:grid-cols-4 gap-4 p-4">
          <Cartao
            titulo="Casos editados que recalcularam"
            valor={r.recalcularam ?? 0}
            rodape={`de ${r.editados ?? 0} editados${r.pct_recalcularam != null ? ` (${r.pct_recalcularam}%)` : ''} · ${
              r.recalc_concluidos ?? 0
            } concluíram`}
          />
          <Cartao
            titulo="Recálculos concluídos"
            valor={x.concluidos ?? 0}
            rodape={`${x.primeira_medicao ?? 0} sem execução anterior para comparar`}
          />
          <Cartao
            titulo="Empates (líquido idêntico)"
            valor={x.empates ?? 0}
            tom="alerta"
            rodape={
              x.pct_empate != null
                ? `${x.pct_empate}% dos ${x.comparaveis ?? 0} comparáveis · ${x.mudou_liquido ?? 0} mudaram o líquido`
                : `sem base comparável · ${x.mudou_liquido ?? 0} mudaram o líquido`
            }
          />
          <Cartao
            titulo="Recálculos cobrados"
            valor={x.cobrados ?? 0}
            rodape={`${x.creditos ?? 0} crédito(s) · ${x.cobrados_com_empate ?? 0} cobrado(s) com empate`}
          />
        </div>

        {/* ledger */}
        <div className="border-t border-slate-100 p-4">
          <div className="text-xs text-slate-500 mb-2">Reprocessos cobrados no ledger</div>
          <div className="flex flex-wrap gap-6">
            <div>
              <div className="text-2xl font-bold text-slate-800">{l.cobrados ?? 0}</div>
              <div className="text-xs text-slate-400">
                cobranças · {l.casos ?? 0} caso(s) · {brl(l.creditos ?? 0)} crédito(s)
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold text-amber-700">{l.sem_ajuste_pendente ?? 0}</div>
              <div className="text-xs text-amber-700/70">
                "sem ajuste pendente" <span className="font-semibold">(aproximação)</span>
              </div>
            </div>
          </div>
          {l.aproximacao && <p className="text-xs text-slate-400 mt-2 max-w-3xl">{l.aproximacao}</p>}
        </div>
      </div>

      {/* ---------------- 2. Funil de uso da ficha ---------------- */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-700">Funil de uso da ficha (por org)</h2>
          <p className="text-xs text-slate-500">
            Orgs distintas em cada etapa — adoção, não sessão. As quatro primeiras etapas vêm da telemetria; as duas
            últimas, de <code>ficha_execucao</code> (o único lugar que sabe se o líquido mudou).
          </p>
        </div>
        <div className="p-4 space-y-1.5">
          {e.funil.map((s) => (
            <div key={s.chave} className="flex items-center gap-3">
              <div className="w-56 text-xs text-slate-600">{s.etapa}</div>
              <div className="flex-1 bg-slate-100 rounded h-4 overflow-hidden">
                <div className="bg-indigo-400 h-4" style={{ width: `${Math.min(100, s.pct_topo ?? 0)}%` }} />
              </div>
              <div className="w-14 text-right text-sm font-semibold text-slate-800">{s.orgs}</div>
              <div className="w-28 text-right text-xs text-slate-400">
                {s.pct_topo != null ? `${s.pct_topo}% topo` : '—'}
                {s.pct_anterior != null ? ` · ${s.pct_anterior}% ant.` : ''}
              </div>
            </div>
          ))}
        </div>

        {/* avisos / cliques / descartes */}
        <div className="border-t border-slate-100 grid grid-cols-1 md:grid-cols-4 gap-4 p-4">
          <Cartao
            titulo={EVENTO_LABEL.ficha_nota_vista}
            valor={notas?.n ?? 0}
            rodape={`${notas?.n_nao_honrado ?? 0} em campo não honrado · ${notas?.orgs ?? 0} org(s)`}
          />
          <Cartao
            titulo={EVENTO_LABEL.ficha_aponta_para_clicked}
            valor={ev.ficha_aponta_para_clicked?.n ?? 0}
            rodape={`${ev.ficha_aponta_para_clicked?.orgs ?? 0} org(s)`}
          />
          <Cartao
            titulo={EVENTO_LABEL.ficha_edit_descartada}
            valor={ev.ficha_edit_descartada?.n ?? 0}
            rodape={`${ev.ficha_edit_descartada?.orgs ?? 0} org(s) · inclui o alias antigo ficha_change_reverted`}
          />
          <Cartao
            titulo={EVENTO_LABEL.ficha_bloqueio_cta}
            valor={ev.ficha_bloqueio_cta?.n ?? 0}
            rodape={`${ev.ficha_bloqueio_cta?.orgs ?? 0} org(s)`}
          />
        </div>

        {/* bloqueios por code */}
        <div className="border-t border-slate-100">
          <div className="px-4 py-2.5 text-xs text-slate-500">Recálculos bloqueados, por motivo (code)</div>
          {e.bloqueios.length === 0 ? (
            <p className="px-4 pb-3 text-xs text-slate-400">Nenhum bloqueio registrado na janela.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-400">
                <tr>
                  <th className="px-4 py-2 text-left">Motivo</th>
                  <th className="px-3 py-2 text-center">Eventos</th>
                  <th className="px-3 py-2 text-center">Orgs</th>
                </tr>
              </thead>
              <tbody>
                {e.bloqueios.map((b) => (
                  <tr key={b.code} className="border-t border-slate-100">
                    <td className="px-4 py-2 text-slate-700 font-mono text-xs">{b.code}</td>
                    <td className="px-3 py-2 text-center font-semibold text-slate-800">{b.n}</td>
                    <td className="px-3 py-2 text-center text-slate-600">{b.orgs}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* CTA por surface */}
        <div className="border-t border-slate-100">
          <div className="px-4 py-2.5 text-xs text-slate-500">CTA da ficha, por surface</div>
          {e.cta.length === 0 ? (
            <p className="px-4 pb-3 text-xs text-slate-400">
              Nenhum evento de CTA na janela (ficha_cta_shown / ficha_cta_clicked).
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-400">
                <tr>
                  <th className="px-4 py-2 text-left">Surface</th>
                  <th className="px-3 py-2 text-center">Mostrado</th>
                  <th className="px-3 py-2 text-center">Clicado</th>
                  <th className="px-3 py-2 text-center">% clique</th>
                  <th className="px-3 py-2 text-center">Orgs</th>
                </tr>
              </thead>
              <tbody>
                {e.cta.map((c) => (
                  <tr key={c.surface} className="border-t border-slate-100">
                    <td className="px-4 py-2 text-slate-700">{c.surface}</td>
                    <td className="px-3 py-2 text-center text-slate-600">{c.shown}</td>
                    <td className="px-3 py-2 text-center font-semibold text-slate-800">{c.clicked}</td>
                    <td className="px-3 py-2 text-center text-slate-600">{c.pct_clique != null ? `${c.pct_clique}%` : '—'}</td>
                    <td className="px-3 py-2 text-center text-slate-600">{c.orgs}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ---------------- 3. Últimos 20 casos editados ---------------- */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-700">Últimos 20 casos editados pela ficha</h2>
          <p className="text-xs text-slate-500">
            Δ líquido só aparece com DUAS execuções concluídas comparáveis. "Empate" = diferença ≤ R$ 0,01.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Caso</th>
                <th className="px-3 py-2 text-right font-medium">Org</th>
                <th className="px-3 py-2 text-right font-medium">Alterações</th>
                <th className="px-3 py-2 text-right font-medium" title="Honradas / não honradas">Honr. / não</th>
                <th className="px-3 py-2 text-center font-medium">Recalculou?</th>
                <th className="px-3 py-2 text-right font-medium">Δ líquido</th>
                <th className="px-3 py-2 text-center font-medium">Cobrado?</th>
                <th className="px-3 py-2 text-center font-medium">Bloqueio?</th>
                <th className="px-3 py-2 text-left font-medium">Última alteração</th>
              </tr>
            </thead>
            <tbody>
              {e.casos.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-4 text-center text-xs text-slate-400">
                    Nenhuma alteração salva pela ficha na janela.
                  </td>
                </tr>
              )}
              {e.casos.map((c) => (
                <tr key={c.processo_id ?? Math.random()} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium text-slate-700">#{c.processo_id ?? '—'}</td>
                  <td className="px-3 py-2 text-right text-slate-500">{c.org_id ?? '—'}</td>
                  <td className="px-3 py-2 text-right text-slate-800 font-medium">{c.n_alteracoes}</td>
                  <td className="px-3 py-2 text-right text-xs">
                    <span className="text-emerald-600">{c.n_honradas}</span>
                    <span className="text-slate-300"> / </span>
                    <span className="text-amber-700">{c.n_nao_honradas}</span>
                    {c.n_sem_marca > 0 && <span className="text-slate-400"> (+{c.n_sem_marca} s/ marca)</span>}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {c.recalculou ? (
                      <span className="text-emerald-600">✓</span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                    {c.n_recalc_concluidos > 0 && (
                      <span className="ml-1 text-xs text-slate-400">{c.n_recalc_concluidos}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-xs">
                    {!c.delta_comparavel ? (
                      <span className="text-slate-300" title="Sem duas execuções concluídas para comparar">—</span>
                    ) : c.empate ? (
                      <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                        empate
                      </span>
                    ) : (
                      <span className="text-slate-700">{brl(c.delta_liquido)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {c.cobrado ? (
                      <span className="text-xs text-slate-700">
                        sim{c.n_cobrancas_ledger > 0 ? ` (${c.n_cobrancas_ledger}×)` : ''}
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {c.bloqueio ? <span className="text-amber-700 text-xs">{c.n_bloqueios}</span> : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-slate-400 text-xs">
                    {c.ultima_alteracao ? c.ultima_alteracao.slice(0, 16).replace('T', ' ') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {e.nota && <p className="px-4 py-3 text-xs text-slate-400 border-t border-slate-100">{e.nota}</p>}
      </div>
    </div>
  );
}

export function EdicaoPage() {
  const { apiParams } = useDateRange();
  const rangeKey = useDateRangeKey();
  const [agg, setAgg] = useState<Agg | null>(null);
  const [loading, setLoading] = useState(true);
  // Efetividade tem janela PRÓPRIA (data da ação), independente do filtro global da aba —
  // ver admin-api/routers/edicao_ficha.py. Por isso estado e fetch separados.
  const [dias, setDias] = useState(30);
  const [efet, setEfet] = useState<Efetividade | null>(null);

  useEffect(() => {
    setLoading(true);
    getEdicao(apiParams).then((r) => setAgg(r.data)).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey]);

  useEffect(() => {
    getEdicaoEfetividade(dias).then((r) => setEfet(r.data)).catch(() => setEfet(null));
  }, [dias]);

  if (loading) return <div className="p-6 text-slate-400">Carregando…</div>;
  if (!agg) return <div className="p-6 text-slate-400">Sem dados de edição ainda.</div>;

  const u = agg.uso;
  const r = agg.reprocesso;
  const f = agg.funciona;
  const m = agg.monetizacao;
  const dist = r.distribuicao || {};

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-800">Edição de cálculo</h1>
        <p className="text-sm text-slate-500">
          Quanto os usuários usam a edição e se ela <b>funciona</b> (gera cálculo com PDF) ou fica sem resultado.
        </p>
      </div>

      {/* 1. Uso da edição */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="rounded-xl border border-slate-200 p-4 bg-white">
          <div className="text-xs text-slate-500">Casos editados</div>
          <div className="text-3xl font-bold text-slate-800">{u.casos_editados}</div>
          <div className="text-xs text-slate-400 mt-1">
            de {u.total_casos} casos na janela · revisão ∪ ficha
            {u.casos_editados_revisao != null && ` · só revisão: ${u.casos_editados_revisao}`}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 p-4 bg-white">
          <div className="text-xs text-slate-500">% de casos editados</div>
          <div className="text-3xl font-bold text-indigo-600">{u.pct_casos_editados ?? '—'}%</div>
          <div className="text-xs text-slate-400 mt-1">sobre o total da janela</div>
        </div>
        <div className="rounded-xl border border-slate-200 p-4 bg-white">
          <div className="text-xs text-slate-500">Orgs que editaram</div>
          <div className="text-3xl font-bold text-slate-800">{u.orgs_editaram}</div>
          <div className="text-xs text-slate-400 mt-1">
            de {u.total_orgs} orgs ativas
            {u.orgs_editaram_revisao != null && ` · só revisão: ${u.orgs_editaram_revisao}`}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 p-4 bg-white">
          <div className="text-xs text-slate-500">% de orgs que editam</div>
          <div className="text-3xl font-bold text-indigo-600">{u.pct_orgs_editam ?? '—'}%</div>
          <div className="text-xs text-slate-400 mt-1">das orgs ativas</div>
        </div>
      </div>

      {/* 1a. FICHA DE LIQUIDAÇÃO — a fonte de edição que faltava na aba.
          A ficha grava onde o executor lê (seis lugares, não só `variavel`), então sem este
          bloco a aba subconta o uso real da edição. Contagem é UNIÃO com interseção explícita:
          somar as duas vias contaria duas vezes o caso editado pelas duas. */}
      {agg.ficha && <FichaBlocoView f={agg.ficha} totalCasos={u.total_casos} totalOrgs={u.total_orgs} />}

      {/* 1c. EFETIVIDADE — a edição pegou? (honrada, mudou o líquido, cobrou). Janela própria
          pela data da AÇÃO, por isso não usa o filtro global da aba. */}
      {efet && <EfetividadeView e={efet} dias={dias} onDias={setDias} />}

      {/* 1b. Conceitos (campos) que os usuários mais editam — pontos fracos do sistema */}
      {agg.conceitos && agg.conceitos.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-700">Conceitos mais editados pelos usuários</h2>
            <p className="text-xs text-slate-500">
              O que os usuários mais corrigem = onde o sistema mais erra/falta. “Vinha vazio” = nosso gap de extração.
            </p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-4 py-2 text-left">Conceito (campo)</th>
                <th className="px-3 py-2 text-center">Casos</th>
                <th className="px-3 py-2 text-center">Orgs</th>
                <th className="px-3 py-2 text-center">Vinha vazio</th>
              </tr>
            </thead>
            <tbody>
              {agg.conceitos.map((c) => (
                <tr key={c.concept} className="border-t border-slate-100">
                  <td className="px-4 py-2 text-slate-700">
                    {CONCEITO_LABEL[c.concept] ?? c.concept}
                    <span className="ml-2 text-xs text-slate-400">{c.concept}</span>
                  </td>
                  <td className="px-3 py-2 text-center font-semibold text-slate-800">{c.n_casos}</td>
                  <td className="px-3 py-2 text-center text-slate-600">{c.n_orgs}</td>
                  <td className="px-3 py-2 text-center text-amber-600">
                    {c.n_vazios > 0 ? `${c.n_vazios} (${Math.round((100 * c.n_vazios) / c.n_casos)}%)` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 3. A edição FUNCIONA? */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="rounded-xl border border-emerald-200 p-4 bg-emerald-50">
          <div className="text-xs text-emerald-700">Editaram e geraram cálculo</div>
          <div className="text-3xl font-bold text-emerald-600">{f.taxa_sucesso ?? '—'}%</div>
          <div className="text-xs text-emerald-700/70 mt-1">{f.sucesso} casos editados terminaram com cálculo (PDF)</div>
        </div>
        <div className="rounded-xl border border-red-200 p-4 bg-red-50">
          <div className="text-xs text-red-700">Editaram e ficaram sem cálculo</div>
          <div className="text-3xl font-bold text-red-600">{f.taxa_abandono ?? '—'}%</div>
          <div className="text-xs text-red-700/70 mt-1">{f.abandono} editaram e NÃO geraram PDF</div>
        </div>
        <div className="rounded-xl border border-slate-200 p-4 bg-white">
          <div className="text-xs text-slate-500">Re-emitiram após editar</div>
          <div className="text-2xl font-bold text-slate-800">{f.reemitiu}</div>
          <div className="text-xs text-slate-400 mt-1">re-gerou o cálculo (inclui histórico)</div>
        </div>
        <div className="rounded-xl border border-slate-200 p-4 bg-white">
          <div className="text-xs text-slate-500">Re-emissões medidas</div>
          <div className="text-2xl font-bold text-slate-800">{f.reemitiu_novo ?? 0}</div>
          <div className="text-xs text-slate-400 mt-1">contador novo (a partir de 28/06)</div>
        </div>
      </div>

      {/* 2. Reprocesso + 4. Monetização */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 p-4 bg-white">
          <div className="text-sm font-semibold text-slate-700 mb-1">Reprocesso</div>
          <div className="text-xs text-slate-400 mb-3">Casos re-emitidos após o original pago.</div>
          <div className="flex gap-6 mb-3">
            <div>
              <div className="text-2xl font-bold text-slate-800">{r.casos_reprocessados}</div>
              <div className="text-xs text-slate-400">casos reprocessados</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-800">{r.total_reprocessos}</div>
              <div className="text-xs text-slate-400">total de reprocessos</div>
            </div>
          </div>
          <div className="space-y-1.5">
            {(['1', '2', '3+'] as const).map((b) => (
              <div key={b} className="flex items-center gap-3">
                <div className="w-20 text-xs text-slate-600">{b} reproc.</div>
                <div className="flex-1 bg-slate-100 rounded h-3.5 overflow-hidden">
                  <div
                    className="bg-indigo-400 h-3.5"
                    style={{
                      width: `${Math.min(100, ((dist[b] || 0) / Math.max(1, r.casos_reprocessados)) * 100)}%`,
                    }}
                  />
                </div>
                <div className="w-12 text-right text-xs text-slate-500">{dist[b] || 0}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 p-4 bg-white">
          <div className="text-sm font-semibold text-slate-700 mb-1">Monetização (reprocesso cobrado)</div>
          <div className="text-xs text-slate-400 mb-3">Transações com descrição "Reprocessamento%".</div>
          <div className="flex gap-8">
            <div>
              <div className="text-3xl font-bold text-slate-800">{m.reprocessos_cobrados}</div>
              <div className="text-xs text-slate-400">reprocessos cobrados</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-emerald-600">{m.creditos_reprocesso}</div>
              <div className="text-xs text-slate-400">créditos gerados por reprocesso</div>
            </div>
          </div>
        </div>
      </div>

      {/* 5. Tabela de casos editados recentes */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 text-sm font-semibold text-slate-700">
          Casos editados recentes ({agg.casos?.length || 0})
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Caso</th>
                <th className="px-3 py-2 text-right font-medium">Org</th>
                <th className="px-3 py-2 text-left font-medium">Via</th>
                <th className="px-3 py-2 text-right font-medium" title="Variáveis editadas pela tela de revisão (exclui as que a própria ficha gravou)">Revisão</th>
                <th className="px-3 py-2 text-right font-medium" title="Edições gravadas pela Ficha de liquidação">Ficha</th>
                <th className="px-3 py-2 text-right font-medium">Reprocessos</th>
                <th className="px-3 py-2 text-center font-medium">PDF?</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-left font-medium">Arquivo</th>
              </tr>
            </thead>
            <tbody>
              {(agg.casos || []).map((c) => (
                <tr key={c.processo_id} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium text-slate-700">#{c.processo_id}</td>
                  <td className="px-3 py-2 text-right text-slate-500">{c.org_id ?? '—'}</td>
                  <td className="px-3 py-2">
                    <span className={
                      c.via === 'ambos' ? 'text-xs px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200'
                        : c.via === 'ficha' ? 'text-xs px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200'
                          : 'text-xs px-1.5 py-0.5 rounded bg-slate-50 text-slate-600 border border-slate-200'
                    }>
                      {c.via === 'ambos' ? 'revisão + ficha' : c.via === 'ficha' ? 'ficha' : 'revisão'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-medium text-slate-800">
                    {(c.n_revisao ?? c.n_editadas) > 0 ? (c.n_revisao ?? c.n_editadas) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-medium text-slate-800">
                    {(c.n_ficha ?? 0) > 0 ? c.n_ficha : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-600">{c.reprocessos}</td>
                  <td className="px-3 py-2 text-center">
                    {c.tem_pdf
                      ? <span className="text-emerald-600">✓</span>
                      : <span className="text-red-400">✕</span>}
                  </td>
                  <td className="px-3 py-2 text-slate-500 text-xs">{c.status || '—'}</td>
                  <td className="px-3 py-2 text-slate-400 text-xs truncate max-w-[220px]" title={c.filename || ''}>
                    {c.filename || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
